export const STRINGS = {
  en: {
    skip: "Skip to main content",
    brandBack: "Encraft Studio — back to top",
    nav: {
      services: "Services",
      approach: "Approach",
      studio: "Studio",
      contact: "Contact",
      requestCall: "Request a Discovery Call",
      requestCallShort: "Request a Call",
      menuOpen: "Open navigation menu",
      menuClose: "Close navigation menu",
      langLabel: "Language",
    },
    hero: {
      overline: "Strategic consulting for growing companies",
      line1: "Complexity,",
      line2: "made",
      line2Em: "beautifully",
      line3: "clear",
      value:
        "We help growing companies make hiring, operations and compliance easier for people to use — and easier to scale.",
      sub: "From HR and recruitment to employer brand, AI-enabled operations and strategic legal design, Encraft Studio turns complicated work into practical ways of working people can use.",
      ctaCall: "Request a Discovery Call",
      ctaExplore: "Explore Services",
      imgAlt: "Curved white architectural ribs — a study of structure and clarity",
    },
    marquee: [
      "Legal Design",
      "AI-Enabled Solutions",
      "Employer Branding",
      "Recruitment Processes",
      "HR Development",
      "Strategic Consulting",
    ],
    services: {
      overline: "What we do",
      h1: "Five crafts,",
      h2pre: "one",
      h2clear: "clear",
      h2post: "way of working",
      intro:
        "Every engagement combines strategy with craft — translating complex requirements into practical solutions your people genuinely use.",
      groups: [
        {
          id: "people",
          label: "People",
          items: [
            {
              n: "01",
              title: "HR & Recruitment-Process Development",
              body: "Structured, human recruitment journeys and HR processes that scale — from the first touchpoint to onboarding and beyond.",
              span: "md:col-span-7",
            },
            {
              n: "02",
              title: "Employer Branding",
              body: "A credible, distinctive employer story that attracts the right people and reflects how you actually work.",
              span: "md:col-span-5",
            },
          ],
        },
        {
          id: "operations",
          label: "Operations",
          items: [
            {
              n: "03",
              title: "Administrative Support",
              body: "A calm, reliable operational backbone: documentation, coordination and day-to-day administration done properly.",
              span: "md:col-span-5",
            },
            {
              n: "04",
              title: "AI-Enabled Solutions",
              body: "Practical, responsible uses of AI woven into your workflows — saving time without losing human judgement.",
              span: "md:col-span-7",
            },
          ],
        },
        {
          id: "clarity-compliance",
          label: "Clarity & Compliance",
          items: [
            {
              n: "05",
              title: "Strategic Legal Design",
              body: "Legal, privacy, information-security and intellectual-property processes made clear and usable — aligned with your operations, customer experience and growth objectives, so compliance readiness and usability improve together.",
              span: "md:col-span-12",
              featured: true,
            },
          ],
        },
      ],
    },
    approach: {
      overline: "Our approach",
      heading: "How we work",
      sub: "A manifesto in four chapters",
      imgAlt: "Colleagues collaborating in a bright modern studio",
      chapters: [
        {
          n: "01",
          title: "Human-centred, always",
          body: "Ways of working only stick when they fit the people using them. We design processes around real behaviour — not org charts.",
        },
        {
          n: "02",
          title: "Complexity, translated",
          body: "Legal, privacy, information-security and IP requirements become clear, usable guidance your teams actually understand.",
        },
        {
          n: "03",
          title: "Compliance as experience",
          body: "We align compliance readiness with your customer experience and operations — so doing the right thing is also the easy thing.",
        },
        {
          n: "04",
          title: "Built to scale with you",
          body: "Every solution is designed to grow alongside your business: practical today, resilient tomorrow.",
        },
      ],
    },
    studio: {
      overline: "The studio",
      headingA: "Strategic partner,",
      headingEm: "human by design",
      body1:
        "Encraft Studio is a strategic consulting practice for small and medium-sized companies. We help you develop clear, user-centred and scalable ways of working — combining HR and recruitment-process development, employer branding, administrative support, AI-enabled solutions and legal design.",
      body2:
        "Our legal-design work is strategic, not transactional. We help organisations make legal, privacy, information-security, intellectual-property and business processes clearer and more usable — better aligned with your operations, your customer experience and your growth.",
      proof: [
        "Focused on small and medium-sized companies",
        "User-centred methods throughout",
        "Compliance-ready outcomes",
        "Solutions built to scale",
      ],
      imgAlt: "Abstract study of light, shadow and structure",
    },
    contact: {
      overline: "Start a conversation",
      headingA: "Let's make your work",
      headingEm: "work better",
      para: "Tell us where the friction is — in your hiring, your brand, your admin, your tools or your legal processes — and we'll show you a clearer way forward.",
      preferEmail: "Prefer email?",
      orConnect: "Or connect on",
      tablistLabel: "Contact options",
      tabEnquiry: "Send an Enquiry",
      tabCall: "Request a Discovery Call",
      tabCallShort: "Request a Call",
      requiredNote: "Required fields",
      labels: {
        name: "Name",
        email: "Email",
        company: "Company (optional)",
        time: "Preferred date & time",
        message: "Message",
        notes: "What would you like to explore? (optional)",
      },
      placeholders: {
        name: "Your full name",
        email: "you@company.com",
        company: "Your company",
        time: "e.g. Weekday mornings, week commencing 3 August",
        message: "Tell us a little about your situation…",
        notes: "A sentence or two is plenty",
      },
      warning:
        "Please do not include sensitive personal information or confidential client material.",
      privacyPre:
        "We'll use the details you provide to respond to your enquiry or arrange the discovery call you request. We do not add you to a marketing list. See our ",
      privacyLink: "Privacy Policy",
      privacyPost: " for details.",
      submitEnquiry: "Send Enquiry",
      submitCall: "Request Discovery Call",
      sending: "Sending…",
      responseNote: "We'll reply within one working day.",
      successTitleEnquiry: "Enquiry received.",
      successTitleCall: "Call requested.",
      successBody:
        "Thank you — your message has been received. We'll reply within one working day.",
      sendAnother: "Send another message",
      error:
        "Sorry — your message couldn't be sent just now. Please try again in a moment, or email hello@encraft.studio directly.",
      toastEnquiry: "Thank you — your enquiry is on its way.",
      toastCall: "Lovely — your discovery call request is in.",
      toastError: "Something went wrong — please try again in a moment.",
      honeypotLabel: "Website",
    },
    footer: {
      descriptor: "Strategic consulting for growing companies.",
      privacy: "Privacy Policy",
      termsComing: "Website Terms — coming soon",
      rights: "Encraft Studio. All rights reserved.",
      tagline: "Privacy-conscious by design.",
      navLabel: "Footer",
    },
    privacy: {
      overline: "Legal",
      title: "Privacy Policy",
      updated: "Last updated: 27 August 2026",
      back: "Back to site",
      blocks: [
        {
          h: "1. Who we are",
          p: [
            "The website is operated by Mindweft Lab, a Finnish sole trader (toiminimi), Business ID 3534017-9, trading under the name Encraft Studio (“Encraft Studio”, “we”, “us” or “our”). Our current registered address is Arielinkatu 2 A 006, 00540 Helsinki, Finland.",
            "Our VAT number is FI35340179.",
            "Mindweft Lab is currently the registered legal name of the business. If the registered name changes to Encraft Studio, this Privacy Policy and the Website Terms will be updated to reflect the new registered name.",
            "Encraft Studio is the controller of the personal data described in this Privacy Policy. You can contact us about privacy by email at hello@encraft.studio, by telephone at +358 45 263 9899, or by writing to the address above.",
          ],
        },
        {
          h: "2. Information we collect",
          p: [
            "When you send an enquiry through the website, we collect your name, email address, optional company name and the message you submit. If you request a discovery call, we also collect your preferred date and time and any optional notes you provide. We also collect information that you choose to send to us by email.",
            "Please do not include sensitive personal information or confidential client material in the free-text fields unless it is genuinely necessary and we have agreed an appropriate method for you to provide it.",
            "Our website hosting may also process limited technical information needed to deliver and protect the website, including IP address, browser and device information, request timestamps, and technical, security or error logs.",
          ],
        },
        {
          h: "3. Why we use your information",
          table: {
            head: ["Purpose", "Personal data used", "Legal basis"],
            rows: [
              [
                "To read and respond to your enquiry, discuss a possible engagement, and arrange a discovery call that you request",
                "Contact details, company, message, preferred date/time and notes",
                "Taking steps at your request before entering into a possible contract, where applicable; otherwise our legitimate interest in receiving and responding to genuine business enquiries",
              ],
              [
                "To operate, maintain and protect the website and investigate errors or misuse",
                "Limited technical, security and error-log data",
                "Our legitimate interest in providing a reliable and secure website",
              ],
              [
                "To comply with law or establish, exercise or defend legal claims",
                "Relevant correspondence, enquiry records and technical records",
                "Compliance with a legal obligation, where applicable, or our legitimate interests in protecting our legal rights",
              ],
            ],
          },
          after: [
            "Submitting the form does not add you to a newsletter or general marketing list. We do not use your enquiry information to market unrelated services to you. If we later introduce marketing that requires consent, we will ask separately.",
          ],
        },
        {
          h: "4. Is providing the information required?",
          p: [
            "You do not have to contact us. If you choose to use a form, the fields marked as required are needed for us to understand and respond to your request. If you do not provide them, we may be unable to reply or arrange a call. Optional fields may be left blank.",
          ],
        },
        {
          h: "5. Who receives your information?",
          p: [
            "Access to your enquiry information is limited to Encraft Studio and the service providers that need it to provide the relevant service. These currently include our EEA-based website-hosting provider and Google Workspace/Gmail, which we use to receive and manage business correspondence and contact-form enquiries in the hello@encraft.studio mailbox.",
            "The contact forms are intended to send submissions directly to that mailbox. We do not store enquiry submissions in a separate website database, Google Drive, customer-relationship management system, marketing automation platform, spreadsheet or separate enquiry backup service.",
            "We do not sell, rent or trade personal data. We do not disclose it to third parties for their independent marketing. We may disclose information where required by law, a court or a competent authority, or where reasonably necessary to protect legal rights or prevent misuse.",
          ],
        },
        {
          h: "6. International transfers",
          p: [
            "Our website hosting is located in the European Economic Area (“EEA”). Google Workspace may, however, transfer, store or allow authorised support access to personal data outside the EEA as part of providing its services. Where this happens, we rely on appropriate safeguards under data-protection law, including the European Commission's standard contractual clauses incorporated in Google's applicable data-processing terms, and any other safeguards required by law.",
            "In simple terms, a non-EEA transfer means that an external provider may handle or access data from a country outside the EU, Iceland, Liechtenstein or Norway. This can occur even where the website server itself is in the EEA. We use Google Workspace only under its business data-processing terms and keep the information in the hello@encraft.studio mailbox limited to what is necessary for the purpose described above.",
          ],
        },
        {
          h: "7. How long we keep information",
          p: [
            "If an enquiry does not lead to an engagement, we delete or anonymise the enquiry and related correspondence within 12 months after our last substantive contact. We may retain a limited record for longer only where necessary to comply with law or to establish, exercise or defend legal claims.",
            "If you become a client, relevant information will become part of the client file and will be retained under the separate retention periods that apply to the engagement, contractual, accounting and legal records.",
            "Technical and security logs are retained for the short period reasonably needed to operate and secure the website and, where under our control, for no longer than 90 days, unless a specific security incident requires relevant records to be preserved for longer. When we delete enquiry emails from our active Google Workspace mailbox, residual copies may remain for a limited period in Google's backup or deletion systems in accordance with its applicable service terms.",
          ],
        },
        {
          h: "8. Security",
          p: [
            "We use proportionate technical and organisational measures designed to protect personal data against unauthorised access, alteration, loss or disclosure. These measures include limiting access to those who need it, using secured accounts and connections, keeping systems appropriately maintained, and using business service providers with appropriate data-protection commitments.",
            "No internet transmission or storage system can be guaranteed to be completely secure. If you believe information sent to us has been compromised, please contact us promptly.",
          ],
        },
        {
          h: "9. Your rights",
          p: [
            "Depending on the circumstances, you may have the following rights under data-protection law:",
          ],
          table: {
            head: ["Right", "What it means"],
            rows: [
              ["Access", "Ask whether we process your personal data and request a copy."],
              ["Rectification", "Ask us to correct inaccurate or incomplete information."],
              ["Erasure", "Ask us to delete information when there is no lawful reason to keep it."],
              ["Restriction", "Ask us to limit processing in specified circumstances."],
              ["Objection", "Object to processing based on legitimate interests."],
              [
                "Portability",
                "Receive certain information you provided in a structured, commonly used and machine-readable format where the legal conditions apply.",
              ],
              ["Complaint", "Complain to the competent data-protection supervisory authority."],
            ],
          },
          after: [
            "To exercise a right, contact hello@encraft.studio. We may ask for information needed to verify your identity. Rights are not absolute, and we will explain if a lawful exception applies.",
            "You may complain to the Office of the Data Protection Ombudsman in Finland. You may also have the right to complain to the supervisory authority in the EU or EEA country where you live or work, or where you believe an infringement occurred.",
          ],
        },
        {
          h: "10. Cookies and similar technologies",
          p: [
            "Encraft Studio does not intend to use non-essential analytics, advertising cookies or session-recording technologies on its production website. The production site may use strictly necessary technologies needed to provide a requested function or maintain security.",
            "Important before launch: the preview version of the website was found to load analytics and tracking-related browser storage. The production website must be checked in a fresh browser to confirm that no such non-essential technology remains. If non-essential cookies, local storage, pixels, beacons, analytics, session recording, or similar technologies are added or retained, they must not load before any consent required by law is obtained, and this Privacy Policy must be updated with accurate information about their provider, purpose, duration and settings controls.",
            "The production website must also be checked for externally hosted fonts, images, video or other embedded content. Such content can cause an external provider to receive technical data such as your IP address when your browser loads the resource. If retained, the provider and processing must be described here; self-hosting such assets is the simplest privacy-conscious approach.",
          ],
        },
        {
          h: "11. External links",
          p: [
            "The website may link to external services, such as LinkedIn. If you follow an external link, the external service will process information under its own privacy terms. A normal link does not by itself mean that we receive information about your activity on that service.",
          ],
        },
        {
          h: "12. Automated decisions",
          p: [
            "We do not use the personal data covered by this Privacy Policy to make decisions based solely on automated processing that produce legal or similarly significant effects.",
          ],
        },
        {
          h: "13. Changes to this policy",
          p: [
            "We may update this Privacy Policy if the website, our service providers or our processing activities change. The current version will be published on this page with its effective date. Material changes will be highlighted where appropriate.",
          ],
        },
        {
          h: "14. Contact",
          p: [
            "For questions or requests concerning this Privacy Policy or your personal data, contact:",
          ],
          lines: [
            "Mindweft Lab, trading as Encraft Studio",
            "Business ID: 3534017-9",
            "VAT number: FI35340179",
            "Address: Arielinkatu 2 A 006, 00540 Helsinki, Finland",
            "Telephone: +358 452639899",
            "Email: hello@encraft.studio",
          ],
        },
      ],
    },
  },
  fi: {
    skip: "Siirry pääsisältöön",
    brandBack: "Encraft Studio — takaisin alkuun",
    nav: {
      services: "Palvelut",
      approach: "Toimintatapa",
      studio: "Studio",
      contact: "Yhteys",
      requestCall: "Pyydä kartoitustapaaminen",
      requestCallShort: "Pyydä tapaaminen",
      menuOpen: "Avaa navigaatiovalikko",
      menuClose: "Sulje navigaatiovalikko",
      langLabel: "Kieli",
    },
    hero: {
      overline: "Strategista konsultointia kasvaville yrityksille",
      line1: "Monimutkaisuus,",
      line2: "tehty",
      line2Em: "kauniin",
      line3: "selkeäksi",
      value:
        "Autamme kasvavia yrityksiä tekemään rekrytoinnista, toiminnasta ja vaatimustenmukaisuudesta helpompaa ihmisille — ja helpompaa skaalata.",
      sub: "HR:stä ja rekrytoinnista työnantajabrändiin, tekoälyavusteiseen toimintaan ja strategiseen legal designiin — Encraft Studio muuttaa monimutkaisen työn käytännön toimintatavoiksi, joita ihmiset todella käyttävät.",
      ctaCall: "Pyydä kartoitustapaaminen",
      ctaExplore: "Tutustu palveluihin",
      imgAlt: "Kaarevia valkoisia arkkitehtonisia rakenteita — tutkielma selkeydestä",
    },
    marquee: [
      "Legal design",
      "Tekoälyratkaisut",
      "Työnantajabrändäys",
      "Rekrytointiprosessit",
      "HR-kehitys",
      "Strateginen konsultointi",
    ],
    services: {
      overline: "Mitä teemme",
      h1: "Viisi osaamisaluetta,",
      h2pre: "yksi",
      h2clear: "selkeä",
      h2post: "tapa toimia",
      intro:
        "Jokainen yhteistyö yhdistää strategian ja huolellisen tekemisen — muutamme monimutkaiset vaatimukset käytännön ratkaisuiksi, joita ihmisesi aidosti käyttävät.",
      groups: [
        {
          id: "people",
          label: "Ihmiset",
          items: [
            {
              n: "01",
              title: "HR- ja rekrytointiprosessien kehitys",
              body: "Rakenteelliset, inhimilliset rekrytointipolut ja HR-prosessit, jotka skaalautuvat — ensimmäisestä kohtaamisesta perehdytykseen ja sen jälkeenkin.",
              span: "md:col-span-7",
            },
            {
              n: "02",
              title: "Työnantajabrändäys",
              body: "Uskottava ja erottuva työnantajatarina, joka houkuttelee oikeita ihmisiä ja kuvastaa sitä, miten teillä todella tehdään töitä.",
              span: "md:col-span-5",
            },
          ],
        },
        {
          id: "operations",
          label: "Toiminta",
          items: [
            {
              n: "03",
              title: "Hallinnollinen tuki",
              body: "Rauhallinen ja luotettava toiminnan selkäranka: dokumentointi, koordinointi ja päivittäinen hallinto tehtynä kunnolla.",
              span: "md:col-span-5",
            },
            {
              n: "04",
              title: "Tekoälyavusteiset ratkaisut",
              body: "Käytännöllinen ja vastuullinen tekoälyn hyödyntäminen osaksi työnkulkuja — ajansäästöä ilman, että inhimillisestä harkinnasta luovutaan.",
              span: "md:col-span-7",
            },
          ],
        },
        {
          id: "clarity-compliance",
          label: "Selkeys ja vaatimustenmukaisuus",
          items: [
            {
              n: "05",
              title: "Strateginen legal design",
              body: "Oikeudelliset, tietosuojaan, tietoturvaan ja immateriaalioikeuksiin liittyvät prosessit tehtyinä selkeiksi ja käyttökelpoisiksi — linjassa toimintanne, asiakaskokemuksenne ja kasvutavoitteidenne kanssa, niin että vaatimustenmukaisuus ja käytettävyys paranevat yhdessä.",
              span: "md:col-span-12",
              featured: true,
            },
          ],
        },
      ],
    },
    approach: {
      overline: "Toimintatapamme",
      heading: "Näin toimimme",
      sub: "Manifesti neljässä luvussa",
      imgAlt: "Kollegoja tekemässä yhteistyötä valoisassa studiolla",
      chapters: [
        {
          n: "01",
          title: "Ihmiskeskeistä, aina",
          body: "Toimintatavat pysyvät vain, kun ne sopivat niitä käyttäville ihmisille. Suunnittelemme prosessit todellisen käyttäytymisen ympärille — emme organisaatiokaavioiden.",
        },
        {
          n: "02",
          title: "Monimutkaisuus käännettynä",
          body: "Oikeudelliset, tietosuojaa, tietoturvaa ja immateriaalioikeuksia koskevat vaatimukset muuttuvat selkeäksi ohjeistukseksi, jonka tiimisi aidosti ymmärtävät.",
        },
        {
          n: "03",
          title: "Vaatimustenmukaisuus kokemuksena",
          body: "Sovitamme vaatimustenmukaisuuden yhteen asiakaskokemuksenne ja toimintanne kanssa — niin, että oikein tekeminen on myös helpoin tapa toimia.",
        },
        {
          n: "04",
          title: "Rakennettu skaalautumaan kanssasi",
          body: "Jokainen ratkaisu suunnitellaan kasvamaan yrityksenne mukana: käytännöllinen tänään, kestävä huomennakin.",
        },
      ],
    },
    studio: {
      overline: "Studio",
      headingA: "Strateginen kumppani,",
      headingEm: "inhimillisesti suunniteltu",
      body1:
        "Encraft Studio on strategiseen konsultointiin keskittyvä toimisto pienille ja keskisuurille yrityksille. Autamme sinua kehittämään selkeitä, käyttäjäkeskeisiä ja skaalautuvia toimintatapoja — yhdistäen HR- ja rekrytointiprosessien kehityksen, työnantajabrändäyksen, hallinnollisen tuen, tekoälyavusteiset ratkaisut ja legal designin.",
      body2:
        "Legal design -työmme on strategista, ei yksittäisten asiakirjojen tekemistä. Autamme organisaatioita tekemään oikeudellisista, tietosuojaa, tietoturvaa, immateriaalioikeuksia ja liiketoimintaa koskevista prosesseista selkeämpiä ja käyttökelpoisempia — paremmin linjassa toimintanne, asiakaskokemuksenne ja kasvunne kanssa.",
      proof: [
        "Keskittynyt pieniin ja keskisuuriin yrityksiin",
        "Käyttäjäkeskeiset menetelmät läpi prosessin",
        "Vaatimustenmukaiset lopputulokset",
        "Ratkaisut, jotka on rakennettu skaalautumaan",
      ],
      imgAlt: "Abstrakti tutkielma valosta, varjosta ja rakenteesta",
    },
    contact: {
      overline: "Aloitetaan keskustelu",
      headingA: "Tehdään työstäsi",
      headingEm: "toimivampaa",
      para: "Kerro, missä kitka on — rekrytoinnissa, brändissä, hallinnossa, työkaluissa tai juridisissa prosesseissa — niin näytämme selkeämmän suunnan eteenpäin.",
      preferEmail: "Sähköpostilla?",
      orConnect: "Tai verkostoidu",
      tablistLabel: "Yhteydenottovaihtoehdot",
      tabEnquiry: "Lähetä yhteydenotto",
      tabCall: "Pyydä kartoitustapaaminen",
      tabCallShort: "Pyydä tapaaminen",
      requiredNote: "Pakolliset kentät",
      labels: {
        name: "Nimi",
        email: "Sähköposti",
        company: "Yritys (valinnainen)",
        time: "Toivottu ajankohta",
        message: "Viesti",
        notes: "Mitä haluaisit pohtia? (valinnainen)",
      },
      placeholders: {
        name: "Etunimi ja sukunimi",
        email: "sinä@yritys.fi",
        company: "Yrityksesi",
        time: "esim. arkiaamuisin, viikolla 32",
        message: "Kerro lyhyesti tilanteestasi…",
        notes: "Muutama lause riittää",
      },
      warning:
        "Älä kirjoita kenttään arkaluonteisia henkilötietoja tai luottamuksellista asiakastietoa.",
      privacyPre:
        "Käytämme antamiasi tietoja vastataksemme yhteydenottoosi tai sopiaksemme pyytämästäsi kartoitustapaamisesta. Emme lisää sinua markkinointilistalle. Lisätietoja on ",
      privacyLink: "tietosuojaselosteessamme",
      privacyPost: ".",
      submitEnquiry: "Lähetä yhteydenotto",
      submitCall: "Pyydä kartoitustapaaminen",
      sending: "Lähetetään…",
      responseNote: "Vastaamme yhden arkipäivän kuluessa.",
      successTitleEnquiry: "Yhteydenotto vastaanotettu.",
      successTitleCall: "Tapaamispyyntö vastaanotettu.",
      successBody:
        "Kiitos — viestisi on vastaanotettu. Vastaamme yhden arkipäivän kuluessa.",
      sendAnother: "Lähetä toinen viesti",
      error:
        "Valitettavasti viestiä ei voitu lähettää juuri nyt. Yritä hetken kuluttua uudelleen tai lähetä sähköpostia osoitteeseen hello@encraft.studio.",
      toastEnquiry: "Kiitos — yhteydenottosi on matkalla.",
      toastCall: "Hienoa — kartoitustapaamispyyntösi on vastaanotettu.",
      toastError: "Jokin meni pieleen — yritä hetken kuluttua uudelleen.",
      honeypotLabel: "Verkkosivu",
    },
    footer: {
      descriptor: "Strategista konsultointia kasvaville yrityksille.",
      privacy: "Tietosuojaseloste",
      termsComing: "Käyttöehdot — tulossa pian",
      rights: "Encraft Studio. Kaikki oikeudet pidätetään.",
      tagline: "Tietosuojalähtöisesti suunniteltu.",
      navLabel: "Alatunniste",
    },
    privacy: {
      overline: "Tietosuoja",
      title: "Tietosuojaseloste",
      updated: "Päivitetty: 27. elokuuta 2026",
      back: "Takaisin sivustolle",
      blocks: [
        {
          h: "1. Keitä olemme",
          p: [
            "Sivustoa ylläpitää Mindweft Lab, suomalainen toiminimi (Y-tunnus 3534017-9), joka käyttää liikenimeä Encraft Studio (”Encraft Studio”, ”me”). Rekisteröity osoitteemme on Arielinkatu 2 A 006, 00540 Helsinki, Suomi.",
            "ALV-tunnisteemme on FI35340179.",
            "Mindweft Lab on yrityksen tämänhetkinen rekisteröity nimi. Jos rekisteröity nimi muuttuu muotoon Encraft Studio, tämä tietosuojaseloste ja käyttöehdot päivitetään vastaamaan uutta rekisteröityä nimeä.",
            "Encraft Studio on tässä tietosuojaselosteessa kuvattujen henkilötietojen rekisterinpitäjä. Voit ottaa meihin yhteyttä tietosuoja-asioissa sähköpostitse osoitteeseen hello@encraft.studio, puhelimitse numeroon +358 45 263 9899 tai kirjeitse yllä olevaan osoitteeseen.",
          ],
        },
        {
          h: "2. Mitä tietoja keräämme",
          p: [
            "Kun lähetät yhteydenoton sivuston kautta, keräämme nimesi, sähköpostiosoitteesi, valinnaisen yrityksen nimen sekä lähettämäsi viestin. Jos pyydät kartoitustapaamista, keräämme lisäksi toivomasi ajankohdan ja mahdolliset lisätietosi. Keräämme myös tiedot, jotka päätät lähettää meille sähköpostitse.",
            "Älä kirjoita vapaaseen tekstikenttään arkaluonteisia henkilötietoja tai luottamuksellista asiakastietoa, ellei se ole aidosti välttämätöntä ja ellei sopivasta tavasta ole erikseen sovittu kanssamme.",
            "Sivuston ylläpito voi lisäksi käsitellä rajallisia teknisiä tietoja, joita tarvitaan sivuston toimittamiseen ja suojaamiseen, kuten IP-osoitetta, selain- ja laitetietoja, pyyntöjen aikaleimoja sekä teknisiä, tietoturva- ja virhelokitietoja.",
          ],
        },
        {
          h: "3. Mihin käytämme tietojasi",
          table: {
            head: ["Tarkoitus", "Käytettävät henkilötiedot", "Oikeusperuste"],
            rows: [
              [
                "Yhteydenottosi lukeminen ja siihen vastaaminen, mahdollisen yhteistyön pohtiminen sekä pyytämäsi kartoitustapaamisen sopiminen",
                "Yhteystiedot, yritys, viesti, toivottu ajankohta ja lisätiedot",
                "Toimenpiteet pyynnöstäsi ennen mahdollisen sopimuksen tekemistä, soveltuvin osin; muutoin oikeutettu etumme vastaanottaa aitoja liiketoimintaan liittyviä yhteydenottoja ja vastata niihin",
              ],
              [
                "Sivuston ylläpitäminen, turvaaminen ja virheiden tai väärinkäytön selvittäminen",
                "Rajalliset tekniset, tietoturva- ja virhelokitiedot",
                "Oikeutettu etumme tarjota luotettava ja turvallinen verkkosivusto",
              ],
              [
                "Lain noudattaminen tai oikeusvaateiden esittäminen, käyttäminen tai puolustaminen",
                "Asiaankuuluva kirjeenvaihto, yhteydenottotiedot ja tekniset tiedot",
                "Lakisääteinen velvollisuus, soveltuvin osin, tai oikeutettu etumme suojata oikeudellisia oikeuksiamme",
              ],
            ],
          },
          after: [
            "Lomakkeen lähettäminen ei lisää sinua uutiskirje- tai yleiselle markkinointilistalle. Emme käytä yhteydenottotietojasi markkinoidaksemme sinulle aiheeseen liittymättömiä palveluita. Jos otamme myöhemmin käyttöön suostumusta edellyttävää markkinointia, pyydämme suostumuksen erikseen.",
          ],
        },
        {
          h: "4. Onko tietojen antaminen pakollista?",
          p: [
            "Sinun ei tarvitse ottaa meihin yhteyttä. Jos päätät käyttää lomaketta, pakollisiksi merkityt kentät ovat välttämättömiä, jotta ymmärrämme pyyntösi ja voimme vastata siihen. Jos et anna niitä, emme välttämättä pysty vastaamaan tai sopimaan tapaamista. Valinnaiset kentät voi jättää tyhjiksi.",
          ],
        },
        {
          h: "5. Kuka saa tietojasi?",
          p: [
            "Pääsy yhteydenottotietoihisi on rajattu Encraft Studioon ja niihin palveluntarjoajiin, jotka tarvitsevat niitä kyseisen palvelun tuottamiseen. Näitä ovat tällä hetkellä ETA-alueella sijaitseva verkkosivuston ylläpitäjämme sekä Google Workspace/Gmail, jota käytämme yrityskirjeenvaihdon ja yhteydenottolomakkeiden viestien vastaanottamiseen ja hallinnointiin hello@encraft.studio-postilaatikossa.",
            "Yhteydenottolomakkeet on tarkoitettu lähettämään viestit suoraan kyseiseen postilaatikkoon. Emme tallenna lomakkeiden lähetyksiä erilliseen verkkosivuston tietokantaan, Google Driveen, asiakkuudenhallintajärjestelmään, markkinoinnin automaatioalustaan, taulukkoon tai erilliseen varmuuskopiointipalveluun.",
            "Emme myy, vuokraa tai vaihda henkilötietoja. Emme luovuta niitä kolmansille osapuolille niiden itsenäistä markkinointia varten. Voimme luovuttaa tietoja, kun laki, tuomioistuin tai toimivaltainen viranomainen sitä edellyttää, tai kun se on kohtuudella tarpeen oikeuksien suojaamiseksi tai väärinkäytön estämiseksi.",
          ],
        },
        {
          h: "6. Kansainväliset tietojensiirrot",
          p: [
            "Verkkosivustomme ylläpito sijaitsee Euroopan talousalueella (”ETA”). Google Workspace voi kuitenkin siirtää, tallentaa tai sallia valtuutetun tuen pääsyn henkilötietoihin ETA:n ulkopuolella osana palveluidensa tuottamista. Kun näin tapahtuu, käytämme tietosuojalainsäädännön mukaisia asianmukaisia suojatoimia, mukaan lukien Euroopan komission vakiosopimuslausekkeet, jotka sisältyvät Googlen soveltuviin tietojenkäsittelyehtoihin, sekä muut lain edellyttämät suojatoimet.",
            "Yksinkertaisesti sanottuna ETA:n ulkopuolinen siirto tarkoittaa, että ulkoinen palveluntarjoaja voi käsitellä tietoja tai päästä niihin EU:n, Islannin, Liechtensteinin tai Norjan ulkopuolisesta maasta. Näin voi tapahtua, vaikka itse verkkosivuston palvelin sijaitsisi ETA:ssa. Käytämme Google Workspacea vain sen yrityskäytön tietojenkäsittelyehtojen mukaisesti ja pidämme hello@encraft.studio-postilaatikon tiedot rajattuina siihen, mikä on tarpeen edellä kuvattuun tarkoitukseen.",
          ],
        },
        {
          h: "7. Kuinka kauan säilytämme tietoja",
          p: [
            "Jos yhteydenotto ei johda yhteistyöhön, poistamme tai anonymisoimme yhteydenoton ja siihen liittyvän kirjeenvaihdon 12 kuukauden kuluessa viimeisestä olennaisesta yhteydenpidosta. Voimme säilyttää rajallisen tiedon pidempään vain, kun se on tarpeen lain noudattamiseksi tai oikeusvaateiden esittämiseksi, käyttämiseksi tai puolustamiseksi.",
            "Jos tulet asiakkaaksemme, asiaankuuluvat tiedot siirtyvät osaksi asiakastiedostoa, ja niitä säilytetään toimeksiantoa, sopimus-, kirjanpito- ja lakiasiakirjoja koskevien erillisten säilytysaikojen mukaisesti.",
            "Tekniset ja tietoturvalokit säilytetään sen lyhyen ajan, joka on kohtuudella tarpeen sivuston ylläpitämiseksi ja turvaamiseksi, ja silloin kun ne ovat hallinnassamme, enintään 90 päivää, ellei tietty tietoturvapoikkeama edellytä asiaankuuluvien tallenteiden säilyttämistä pidempään. Kun poistamme yhteydenottojen sähköpostit aktiivisesta Google Workspace -postilaatikosta, jäljelle jääviä kopioita voi säilyä rajoitetun ajan Googlen varmuuskopiointi- tai poistojärjestelmissä sen soveltuvien palveluehtojen mukaisesti.",
          ],
        },
        {
          h: "8. Tietoturva",
          p: [
            "Käytämme oikeasuhteisia teknisiä ja organisatorisia toimenpiteitä, joiden tarkoituksena on suojata henkilötietoja luvattomalta pääsyltä, muuttamiselta, katoamiselta tai luovuttamiselta. Näitä ovat muun muassa pääsyn rajaaminen niille, jotka sitä tarvitsevat, suojattujen tilien ja yhteyksien käyttäminen, järjestelmien asianmukainen ylläpito sekä sellaisten yrityspalveluntarjoajien käyttäminen, joilla on asianmukaiset tietosuojasitoumukset.",
            "Mikään internetin välityksellä tapahtuva tiedonsiirto tai tallennusjärjestelmä ei ole täysin turvallinen. Jos epäilet, että meille lähettämäsi tiedot ovat joutuneet vääriin käsiin, ole meihin viipymättä yhteydessä.",
          ],
        },
        {
          h: "9. Oikeutesi",
          p: [
            "Olosuhteista riippuen sinulla voi olla seuraavat tietosuojalainsäädännön mukaiset oikeudet:",
          ],
          table: {
            head: ["Oikeus", "Mitä se tarkoittaa"],
            rows: [
              ["Pääsy tietoihin", "Voit kysyä, käsittelemmekö henkilötietojasi, ja pyytää niistä kopion."],
              ["Tietojen oikaisu", "Voit pyytää meitä korjaamaan virheelliset tai puutteelliset tiedot."],
              ["Tietojen poistaminen", "Voit pyytää meitä poistamaan tiedot, kun niiden säilyttämiseen ei ole laillista perustetta."],
              ["Käsittelyn rajoittaminen", "Voit pyytää meitä rajoittamaan käsittelyä tietyissä tilanteissa."],
              ["Vastustaminen", "Voit vastustaa oikeutettuun etuun perustuvaa käsittelyä."],
              [
                "Siirrettävyys",
                "Voit saada tietyt antamasi tiedot jäsennellyssä, yleisesti käytetyssä ja koneellisesti luettavassa muodossa, kun lailliset edellytykset täyttyvät.",
              ],
              ["Valitus", "Voit tehdä valituksen toimivaltaiselle tietosuojan valvontaviranomaiselle."],
            ],
          },
          after: [
            "Jos haluat käyttää oikeuksiasi, ota yhteyttä osoitteeseen hello@encraft.studio. Voimme pyytää henkilöllisyytesi varmentamiseen tarvittavia tietoja. Oikeudet eivät ole ehdottomia, ja kerromme, jos laillinen poikkeus soveltuu.",
            "Voit tehdä valituksen Suomen tietosuojavaltuutetun toimistoon. Sinulla voi myös olla oikeus tehdä valitus sen EU- tai ETA-maan valvontaviranomaiselle, jossa asut tai työskentelet, tai jossa epäilet rikkomuksen tapahtuneen.",
          ],
        },
        {
          h: "10. Evästeet ja vastaavat teknologiat",
          p: [
            "Encraft Studio ei aio käyttää ei-välttämättömiä analytiikka-, mainonta- tai istunnontallennusteknologioita verkkosivustonsa tuotantoversiossa. Tuotantosivusto voi käyttää tiukasti välttämättömiä teknologioita, joita tarvitaan pyydetyn toiminnon tarjoamiseen tai tietoturvan ylläpitämiseen.",
            "Tärkeää ennen julkaisua: sivuston esikatseluversion havaittiin lataavan analytiikkaan ja seurantaan liittyvää selaimen tallennustilaa. Tuotantosivusto on tarkistettava uudessa selainistunnossa sen varmistamiseksi, ettei tällaisia ei-välttämättömiä teknologioita ole jäljellä. Jos ei-välttämättömiä evästeitä, paikallistallennusta, pikseleitä, analytiikkaa, istunnontallennusta tai vastaavia teknologioita lisätään tai säilytetään, niitä ei saa ladata ennen lain edellyttämän suostumuksen saamista, ja tämä tietosuojaseloste on päivitettävä tarkoilla tiedoilla niiden tarjoajasta, tarkoituksesta, kestosta ja asetusten hallinnasta.",
            "Tuotantosivusto on myös tarkistettava ulkoisesti isännöityjen fonttien, kuvien, videoiden ja muun upotetun sisällön osalta. Tällainen sisältö voi aiheuttaa sen, että ulkoinen palveluntarjoaja vastaanottaa teknisiä tietoja, kuten IP-osoitteesi, kun selaimesi lataa resurssin. Jos ne jäävät käyttöön, palveluntarjoaja ja käsittely on kuvattava tässä; tällaisen sisällön itse isännöinti on yksinkertaisin tietosuojaa kunnioittava lähestymistapa.",
          ],
        },
        {
          h: "11. Ulkoiset linkit",
          p: [
            "Sivusto voi linkittää ulkoisiin palveluihin, kuten LinkedIniin. Jos seuraat ulkoista linkkiä, kyseinen palvelu käsittelee tietojasi omien tietosuojaehtojensa mukaisesti. Tavallinen linkki ei sinällään tarkoita, että saisimme tietoa toiminnastasi kyseisessä palvelussa.",
          ],
        },
        {
          h: "12. Automatisoidut päätökset",
          p: [
            "Emme käytä tämän tietosuojaselosteen kattamia henkilötietoja sellaisten päätösten tekemiseen, jotka perustuvat yksinomaan automatisoituun käsittelyyn ja joilla on oikeusvaikutuksia tai muuten vastaavanlaisia merkittäviä vaikutuksia.",
          ],
        },
        {
          h: "13. Muutokset tähän selosteeseen",
          p: [
            "Voimme päivittää tätä tietosuojaselostetta, jos verkkosivusto, palveluntarjoajamme tai käsittelytoimemme muuttuvat. Voimassa oleva versio julkaistaan tällä sivulla voimaantulopäivämäärineen. Olennaisista muutoksista ilmoitetaan tarvittaessa näkyvästi.",
          ],
        },
        {
          h: "14. Yhteystiedot",
          p: [
            "Tähän tietosuojaselosteeseen tai henkilötietoihisi liittyvissä kysymyksissä tai pyynnöissä ota yhteyttä:",
          ],
          lines: [
            "Mindweft Lab, liikenimenä Encraft Studio",
            "Y-tunnus: 3534017-9",
            "ALV-tunniste: FI35340179",
            "Osoite: Arielinkatu 2 A 006, 00540 Helsinki, Suomi",
            "Puhelin: +358 452639899",
            "Sähköposti: hello@encraft.studio",
          ],
        },
      ],
    },
  },
};
